import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import conversationService, { ConversationBusyError } from '../services/conversation.service';
import type {
  ConversationMessage,
  ConversationListItem,
  ConversationStatus,
  PollLatestResponse,
} from '../types/conversation.types';
import { showToast } from '../utils/toast';
import { collectSupportContext, formatViewport } from '../utils/support-context';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 90_000;

interface ChatMessage {
  id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  created_at: string;
  attachments?: { key: string; filename: string; size: number; content_type: string }[];
}

interface SupportChatContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  messages: ChatMessage[];
  conversationId: string | null;
  conversationStatus: ConversationStatus | null;
  supportRequestId: string | null;
  isPending: boolean;
  pollElapsed: number;
  unreadCount: number;

  sendMessage: (content: string, files?: File[]) => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (id: string) => Promise<void>;

  conversations: ConversationListItem[];
  loadConversations: () => Promise<void>;
  conversationsLoading: boolean;

  activeView: 'chat' | 'list';
  setActiveView: (view: 'chat' | 'list') => void;
}

const SupportChatContext = createContext<SupportChatContextType | null>(null);

export const SupportChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationStatus, setConversationStatus] = useState<ConversationStatus | null>(null);
  const [supportRequestId, setSupportRequestId] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [pollElapsed, setPollElapsed] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeView, setActiveView] = useState<'chat' | 'list'>('chat');

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const convIdRef = useRef<string | null>(null);

  // Keep refs in sync for use inside setInterval closures
  useEffect(() => { convIdRef.current = conversationId; }, [conversationId]);
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    setIsPending(false);
    setPollElapsed(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback((convId: string) => {
    stopPolling();
    setIsPending(true);
    setPollElapsed(0);
    pollStartRef.current = Date.now();

    // Elapsed timer for progressive indicator
    elapsedRef.current = setInterval(() => {
      setPollElapsed(Date.now() - pollStartRef.current);
    }, 1000);

    let consecutiveErrors = 0;

    pollRef.current = setInterval(async () => {
      // Timeout check
      if (Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) {
        stopPolling();
        setMessages(prev => [...prev, {
          id: `system-timeout-${Date.now()}`,
          role: 'system',
          content: 'Не удалось получить ответ. Попробуйте ещё раз или создайте обращение вручную.',
          created_at: new Date().toISOString(),
        }]);
        return;
      }

      try {
        const result: PollLatestResponse = await conversationService.pollLatestMessage(convId);
        consecutiveErrors = 0;

        if (result.status === 'completed') {
          stopPolling();

          if (result.agent_message) {
            setMessages(prev => [...prev, {
              id: `agent-${Date.now()}`,
              role: 'agent',
              content: result.agent_message!,
              created_at: new Date().toISOString(),
            }]);
          }

          if (result.conversation_status) {
            setConversationStatus(result.conversation_status);
          }

          if (result.support_request_id) {
            setSupportRequestId(result.support_request_id);
            setMessages(prev => [...prev, {
              id: `system-escalated-${Date.now()}`,
              role: 'system',
              content: `Обращение создано. Администратор получит его с полным контекстом вашего диалога.`,
              created_at: new Date().toISOString(),
            }]);
          }

          // Increment unread if panel is closed (use ref to avoid stale closure)
          if (!isOpenRef.current) {
            setUnreadCount(prev => prev + 1);
          }
        }
      } catch {
        consecutiveErrors++;
        if (consecutiveErrors >= 3) {
          stopPolling();
          showToast('Ошибка подключения. Попробуйте ещё раз.', 'error');
        }
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const open = useCallback(() => {
    setIsOpen(true);
    setUnreadCount(0);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) setUnreadCount(0);
      return !prev;
    });
  }, []);

  const sendMessage = useCallback(async (content: string, files?: File[]) => {
    if (!content.trim() && (!files || files.length === 0)) return;
    if (isPending) return;

    let currentConvId = convIdRef.current;

    // Create conversation if none exists
    if (!currentConvId) {
      try {
        const ctx = collectSupportContext();
        const metadata = {
          url: ctx.url,
          browser: ctx.userAgent,
          viewport: formatViewport(ctx.viewport),
        };
        const conv = await conversationService.createConversation({ metadata_json: metadata });
        currentConvId = conv.id;
        setConversationId(conv.id);
        setConversationStatus('active');

        // Add greeting as agent message
        if (conv.greeting_message) {
          setMessages([{
            id: `agent-greeting-${conv.id}`,
            role: 'agent',
            content: conv.greeting_message,
            created_at: new Date().toISOString(),
          }]);
        }
      } catch {
        showToast('Не удалось начать диалог. Попробуйте позже.', 'error');
        return;
      }
    }

    // Add user message optimistically (with local file previews)
    const optimisticAttachments = files?.map(f => ({
      key: URL.createObjectURL(f),
      filename: f.name,
      size: f.size,
      content_type: f.type,
    }));

    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
      attachments: optimisticAttachments,
    }]);

    // Send to API
    try {
      await conversationService.sendMessage(currentConvId, content, files);
      startPolling(currentConvId);
    } catch (err) {
      if (err instanceof ConversationBusyError) {
        showToast('Подождите, агент обрабатывает предыдущее сообщение.', 'warning');
      } else {
        showToast('Сообщение не отправлено. Попробуйте ещё раз.', 'error');
        // Remove optimistic message
        setMessages(prev => prev.slice(0, -1));
      }
    }
  }, [isPending, startPolling]);

  const startNewConversation = useCallback(() => {
    stopPolling();
    setMessages([]);
    setConversationId(null);
    setConversationStatus(null);
    setSupportRequestId(null);
    setActiveView('chat');
  }, [stopPolling]);

  const loadConversation = useCallback(async (id: string) => {
    try {
      const conv = await conversationService.getConversation(id);
      setConversationId(conv.id);
      setConversationStatus(conv.status);
      setSupportRequestId(conv.support_request_id);
      setMessages(conv.messages.map((m: ConversationMessage) => ({
        id: m.id,
        role: m.role === 'agent' ? 'agent' as const : m.role === 'system' ? 'system' as const : 'user' as const,
        content: m.content,
        created_at: m.created_at,
        attachments: m.attachments_json ?? undefined,
      })));
      setActiveView('chat');
    } catch {
      showToast('Не удалось загрузить диалог.', 'error');
    }
  }, []);

  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const result = await conversationService.listConversations({ limit: 20 });
      setConversations(result.items);
    } catch {
      showToast('Не удалось загрузить список диалогов.', 'error');
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  return (
    <SupportChatContext.Provider value={{
      isOpen, open, close, toggle,
      messages, conversationId, conversationStatus, supportRequestId,
      isPending, pollElapsed, unreadCount,
      sendMessage, startNewConversation, loadConversation,
      conversations, loadConversations, conversationsLoading,
      activeView, setActiveView,
    }}>
      {children}
    </SupportChatContext.Provider>
  );
};

export const useSupportChat = (): SupportChatContextType => {
  const context = useContext(SupportChatContext);
  if (!context) {
    throw new Error('useSupportChat must be used within SupportChatProvider');
  }
  return context;
};
